import { z } from 'zod'

export * as driveLocales from './locales'

/**
 * Drive (apps/drive, apps/api/src/modules/drive): everyone's files, kept in folders. A drive holds only folders at its
 * top (โฟลเดอร์แรก): a file always sits in a folder. A top folder is its owner's and is shared as a whole: who may see it
 * is set there only (a person, a branch, a department or the whole company, each as a viewer, uploader, deleter or
 * editor) and everything inside follows. Anyone outside the company opens a file or a folder by its link, until the link
 * ends.
 */

/**
 * Everyone sees, opens and downloads; an uploader also adds (files, subfolders, new versions) and changes what they
 * added; a deleter also takes anyone's away; an editor is both, and renames or moves anyone's too
 */
export const driveRoles = ['viewer', 'uploader', 'deleter', 'editor'] as const
export type DriveRole = (typeof driveRoles)[number]

/** Where one stands in a top folder: its owner, or a role given to them (or their branch, department, the company) */
export type DriveAccess = DriveRole | 'owner'

/** What each may do beyond looking: add, take anyone's away (`delete`), rename or move anyone's (`change`), share */
const rights: Record<DriveAccess, { upload: boolean; delete: boolean; change: boolean; share: boolean }> = {
  viewer: { upload: false, delete: false, change: false, share: false },
  uploader: { upload: true, delete: false, change: false, share: false },
  deleter: { upload: false, delete: true, change: false, share: false },
  editor: { upload: true, delete: true, change: true, share: false },
  owner: { upload: true, delete: true, change: true, share: true },
}
export type DriveRight = keyof (typeof rights)['owner']

/** May one who stands so do this (none: not there at all) */
export const driveMay = (access: DriveAccess | null, right: DriveRight) => !!access && rights[access][right]

/**
 * Two ways in, together: the owner stays the owner, an uploader's and a deleter's make an editor (a person given one by
 * their branch and the other by their department), otherwise the one that may more (none: null)
 */
export function bothAccess(a: DriveAccess | null, b: DriveAccess | null): DriveAccess | null {
  if (!a || !b) return a ?? b
  if (a === 'owner' || b === 'owner') return 'owner'
  const upload = rights[a].upload || rights[b].upload
  const remove = rights[a].delete || rights[b].delete
  return upload && remove ? 'editor' : upload ? 'uploader' : remove ? 'deleter' : 'viewer'
}

/**
 * May one change something inside a top folder (not the top folder itself, its owner's): rename or move it (`change`),
 * or take it to the trash (`delete`). Anyone's by the right itself; what one added oneself also as an uploader, and to
 * the trash as anyone who may add or take away
 */
export function driveMayChange(access: DriveAccess | null, what: 'change' | 'delete', mine: boolean) {
  if (driveMay(access, what)) return true
  if (!mine) return false
  return what === 'change' ? driveMay(access, 'upload') : driveMay(access, 'upload') || driveMay(access, 'delete')
}

/** Who a top folder is shared with: a person (their account), a branch, a department, or everyone at the company */
export const driveGrantKinds = ['user', 'branch', 'department', 'company'] as const
export type DriveGrantKind = (typeof driveGrantKinds)[number]
/** The company's grant names no one: its target is this */
export const DRIVE_COMPANY = '*'

export const driveItemKinds = ['folder', 'file'] as const
export type DriveItemKind = (typeof driveItemKinds)[number]

/** One file at most (a phone's video fits) */
export const DRIVE_FILE_MAX = 2 * 1024 ** 3
/** Each person's room: every version of every file in the top folders they own, the trash too */
export const DRIVE_QUOTA = 15 * 1024 ** 3
/** Things in the trash go for good after this */
export const DRIVE_TRASH_DAYS = 30
/** How long an outsider's link may last */
export const driveLinkDays = [7, 30, 90] as const
export const DRIVE_NAME_MAX = 200

/** Shown in the web (Preview): Word and Excel are drawn in the page; PowerPoint and anything else are downloaded */
export const drivePreviews = {
  pdf: ['application/pdf'],
  image: ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/heic', 'image/heif'],
  video: ['video/mp4', 'video/webm', 'video/quicktime'],
  audio: ['audio/mpeg', 'audio/mp4', 'audio/wav', 'audio/webm', 'audio/ogg'],
  text: ['text/plain', 'text/csv'],
  word: ['application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
  excel: ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
} as const
export type DrivePreview = keyof typeof drivePreviews

/** How a file of this type shows in the web, or null: it is only downloaded */
export function drivePreviewOf(type: string): DrivePreview | null {
  for (const [kind, types] of Object.entries(drivePreviews)) if ((types as readonly string[]).includes(type)) return kind as DrivePreview
  return null
}

/** What the browser may open in place (from the bucket's own address): anything else always comes as a download */
export const driveOpensInPlace = (type: string) => ['pdf', 'image', 'video', 'audio', 'text'].includes(drivePreviewOf(type) ?? '')

const name = z
  .string()
  .trim()
  .min(1, 'กรุณาตั้งชื่อ')
  .max(DRIVE_NAME_MAX)
  .refine((v) => !/[\\/]/.test(v), 'ชื่อมี / หรือ \\ ไม่ได้')

/** A new folder: at the top of my drive (`parentId` null), or inside one */
export const driveFolderInput = z.object({ name, parentId: z.string().min(1).nullable() })
export type DriveFolderInput = z.input<typeof driveFolderInput>

/** Renaming, or moving into another folder of the same top folder (one's own top folders: any of them) */
export const driveItemPatch = z.object({ name: name.optional(), parentId: z.string().min(1).optional() })
export type DriveItemPatch = z.input<typeof driveItemPatch>

/** A file about to be sent straight to storage: into a folder, or as a file's new version */
export const driveUploadInput = z.object({
  folderId: z.string().min(1),
  /** A file's new version, keeping its name */
  replacesId: z.string().min(1).nullish(),
  name,
  type: z.string().trim().max(200).default(''),
  size: z
    .number()
    .int()
    .min(0)
    .max(DRIVE_FILE_MAX, `ไฟล์ใหญ่เกิน ${DRIVE_FILE_MAX / 1024 ** 3} GB`),
})
export type DriveUploadInput = z.input<typeof driveUploadInput>

export const driveGrantInput = z.object({
  kind: z.enum(driveGrantKinds),
  /** The person's account, the branch, the department; the company: `DRIVE_COMPANY` */
  targetId: z.string().min(1),
  role: z.enum(driveRoles),
})
export type DriveGrantInput = z.input<typeof driveGrantInput>

/** Who may see a top folder, all of them at once (the owner is not among them) */
export const driveGrantsInput = z.object({ grants: z.array(driveGrantInput).max(200) })

export const driveLinkInput = z.object({
  days: z.union([z.literal(7), z.literal(30), z.literal(90)]).default(30),
  /** Outsiders may download it; otherwise they only see what opens in the page */
  download: z.boolean().default(true),
})
export type DriveLinkInput = z.input<typeof driveLinkInput>

export interface DrivePerson {
  id: string
  name: string
}

/** A folder or a file as a list shows it */
export interface DriveItem {
  id: string
  kind: DriveItemKind
  name: string
  parentId: string | null
  rootId: string
  /** A file's current version */
  type: string | null
  size: number | null
  /** Who made the folder or sent the file's first version */
  createdBy: DrivePerson | null
  createdAt: string
  updatedBy: DrivePerson | null
  updatedAt: string
  starred: boolean
  /** In the trash: since when, and by whom */
  trashedAt: string | null
  trashedBy?: DrivePerson | null
  /** Where it is (lists that mix folders: recent, starred, trash): its top folder, then its folder */
  where?: string
  /** An outsider's link is on */
  linked?: boolean
}

/** Whom a top folder is shared with, by name (the company's is its kind's) */
export interface DriveShare {
  kind: DriveGrantKind
  name: string
}

/** A top folder in my drive, or one shared with me */
export interface DriveRoot {
  id: string
  name: string
  owner: DrivePerson | null
  access: DriveAccess
  /** Whom it is shared with (none: only its owner) */
  sharedWith: DriveShare[]
  /** Shared with me: how it reached me (me, my branch, my department, the company) and when */
  via?: DriveShare
  sharedAt?: string
  updatedAt: string
  starred: boolean
}

/** A folder opened: where it is, what I may do, and what is in it */
export interface DriveFolder {
  item: DriveItem
  /** From the top folder down to this one */
  path: { id: string; name: string }[]
  root: { id: string; name: string; owner: DrivePerson | null }
  access: DriveAccess
  /** The top folder's grants, for those inside to see who else does */
  sharedWith: DriveShare[]
  children: DriveItem[]
}

export interface DriveGrant extends DriveTarget {
  role: DriveRole
}

export interface DriveVersion {
  id: string
  number: number
  name: string
  size: number
  type: string
  createdAt: string
  createdBy: DrivePerson | null
  current: boolean
}

export interface DriveLink {
  token: string
  url: string
  expiresAt: string
  download: boolean
}

/** Someone or something a top folder may be shared with, as the picker offers it */
export interface DriveTarget {
  kind: DriveGrantKind
  targetId: string
  /** The person's, branch's or department's (the company's: empty, its kind names it) */
  name: string
  /** A person's branch */
  sub?: string
}

/** Where storage is sent a file: a link to PUT it to (with these headers), then `done` */
export interface DriveUploadTicket {
  key: string
  url: string
  headers: Record<string, string>
}

export interface DriveRoom {
  used: number
  quota: number
}

/** An outsider's link opened */
export interface DriveShared {
  sharedBy: string
  company: string
  expiresAt: string
  download: boolean
  item: Pick<DriveItem, 'id' | 'kind' | 'name' | 'type' | 'size' | 'updatedAt'>
  /** A folder's: where one is in it, and what is there */
  path?: { id: string; name: string }[]
  children?: Pick<DriveItem, 'id' | 'kind' | 'name' | 'type' | 'size' | 'updatedAt'>[]
}
